<template>
  <div class="mc-videohistory-player__container">
    <div class="mc-videohistory--video-wrapper">
      <div class="mc-videohistory-stream">
        <h4 class="mc-videohistory-stream__title">
          {{ $store.state.vehicle.cameraLabel }}
        </h4>
        <div class="mc-videohistory-stream__wrapper" :class="{ isLoading: isLoadingVideoPlayer }">
          <template v-if="isLoadingVideoPlayer">
            <b-spinner />
          </template>
          <template v-else>
            <EndVideo
              v-show="$store.state.vehicle.videoState === 'ended'"
              :isMdvrSleep="isMdvrSleep"
            />
            <Sleep
              v-if="$store.state.vehicle.videoState === 'sleep'"
              :imei="$store.state.vehicle.vehicleDetail.imei"
              @countdownEnd="countdownSleepEnd"
              @sendCommandStream="sendCommandStream"
            />
            <MeVideo
              v-if="$store.state.vehicle.source && $store.state.vehicle.videoState !== 'ended'"
              v-show="
                $store.state.vehicle.videoState === 'ready' ||
                $store.state.vehicle.videoState === 'pause'
              "
              ref="videoPlayerHistory"
              :source="$store.state.vehicle.source"
              :isMdvrSleep="isMdvrSleep"
              @ready="onPlayerReady"
              @error="onPlayerError"
              @pause="pauseVideo"
              @mute="muteVideo"
              @startPlay="onStartPlay"
              @sleep="onPlayerSleep"
            />
            <NoFeedBack
              v-show="$store.state.vehicle.videoState === 'error'"
              @clickTryAgain="clickTryAgain"
            />
            <Connecting v-show="$store.state.vehicle.videoState === 'connecting'" />
            <NoVideo v-show="$store.state.vehicle.videoState === 'noVideo'" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import responsive from '@/mixin/responsive'
import NoFeedBack from './State/NoFeedBack'
import Connecting from './State/Connecting'
import NoVideo from './State/NoVideo'
import MeVideo from './State/MeVideo'
import EndVideo from './State/End.vue'
import Sleep from './State/Sleep.vue'
import { mapGetters, mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'VideoHistoryPlayerV2',
  components: {
    NoFeedBack,
    Connecting,
    NoVideo,
    MeVideo,
    EndVideo,
    Sleep,
  },
  mixins: [MixPanel, responsive, Helper],

  data() {
    return {
      errorWhilePlay: false,
    }
  },
  computed: {
    ...mapState('vehicle', ['isLoadingVideoPlayer']),
    ...mapGetters('vehicle', ['vehicleDetail', 'vehicleStatusByVehicleId', 'isUsingWsFlv']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isVideoDemo() {
      if (!this.$route.query?.vehicleId) {
        return false
      }
      const dataDemoVideoHistory = this.getFeatureFlagValue('vsms2_trackvision2_playback_demo_id')
      return Object.keys(dataDemoVideoHistory).includes(this.$route.query?.vehicleId.toString())
    },
    isMdvrSleep() {
      return (
        this.isDeviceBsjMdvr(this.vehicleDetail.deviceCode) &&
        this.getVehicleStatus?.calculatedMotionStatus === 'P'
      )
    },
    getVehicleStatus() {
      const vehicleId = parseInt(this.$route.query?.vehicleId)
      if (!vehicleId) return null
      const vehicleStatus = this.vehicleStatusByVehicleId(vehicleId)
      if (!vehicleStatus) return null
      if (this.isVideoDemo) {
        return {
          ...vehicleStatus,
          calculatedMotionStatus: 'M',
        }
      }
      return vehicleStatus
    },
  },

  watch: {
    '$store.state.vehicle.videoState'(newVideoState, oldVideoState) {
      if (this.isVideoDemo) {
        const refsName = `videoPlayerDemo${this.$store.state.vehicle.vehicleDetail.imei}`
        if (newVideoState === 'pause') {
          this.$refs.videoPlayerHistory.$refs[refsName].player.pause()
        }
        if (oldVideoState === 'pause' && newVideoState === 'ready') {
          this.$refs.videoPlayerHistory.$refs[refsName].player.play()
        }
        return
      }
      const refsName = `videoPlayer${this.$store.state.vehicle.vehicleDetail.imei}`
      if (newVideoState === 'pause') {
        this.$refs.videoPlayerHistory.$refs[refsName].flv.pause()
      }
      if (oldVideoState === 'pause' && newVideoState === 'ready') {
        this.$refs.videoPlayerHistory.$refs[refsName].flv.play()
      }
      if (newVideoState === 'error' && oldVideoState === 'ready') {
        this.errorWhilePlay = true
      }
    },
  },

  methods: {
    async onStartPlay() {
      const video =
        this.$store.state.vehicle.vehicleVideos[this.$store.state.vehicle.indexVideoActive[0]]
          .videos[this.$store.state.vehicle.indexVideoActive[1]]
      const deviceName = this.vehicleDetail.deviceName
      const input =
        deviceName === 'jc'
          ? {
              jcInput: {
                filename: video.mediaFilename,
              },
            }
          : {
              bsjInput: {
                channel: `CH${this.addLeadingZero(video.channel)}`,
                endTime: this.formatDateToUtc(video.endTime),
                startTime: this.formatDateToUtc(video.startTime),
              },
            }

      await this.$store.dispatch('vehicle/requestStreamFromDevice', {
        status: true,
        imei: this.vehicleDetail.imei,
        statsDataV2: {
          tvType: 'TV2',
          idPlay: this.$store.state.vehicle.playbackUidActive,
        },
        ...input,
      })
    },
    onPlayerSleep() {
      if (!this.isMdvrSleep) return
      this.$store.commit('vehicle/setVideoStatusDetail', 'sleep')
      this.$forceUpdate()
    },
    countdownSleepEnd() {
      const videoRef = this.$refs.videoPlayerHistory
      if (videoRef && videoRef.countdownSleepEnd) {
        videoRef.countdownSleepEnd()
      }
      this.onPlayerError()
    },
    onPlayerReady() {
      setTimeout(() => {
        this.$store.commit('vehicle/setVideoStatusDetail', 'ready')
        this.$forceUpdate()
        var refsName = `videoPlayer${this.$store.state.vehicle.vehicleDetail.imei}`
        this.$refs.videoPlayerHistory.$refs[refsName].player.muted = true
      }, 300)
    },
    onPlayerError() {
      setTimeout(() => {
        this.$store.commit('vehicle/setVideoStatusDetail', 'error')
        this.$forceUpdate()
        this.requestOnlineCommandStatsDataTryAgain()
      }, 300)
    },
    pauseVideo() {},
    muteVideo() {
      // var refsName = this.isVideoDemo
      //   ? `videoPlayerDemo${this.$store.state.vehicle.vehicleDetail.imei}`
      //   : `videoPlayer${this.$store.state.vehicle.vehicleDetail.imei}`

      var refsName = `videoPlayer${this.$store.state.vehicle.vehicleDetail.imei}`
      this.$store.commit('vehicle/setVideoStatusDetail', 'ready')
      this.$forceUpdate()
      this.$refs.videoPlayerHistory.$refs[refsName].player.muted =
        !this.$refs.videoPlayerHistory.$refs[refsName].player.muted
    },

    async clickTryAgain() {
      if (!this.errorWhilePlay) {
        const deviceType = this.$store.state.vehicle.vehicleDetail.deviceName
        const video =
          this.$store.state.vehicle.vehicleVideos[this.$store.state.vehicle.indexVideoActive[0]]
            .videos[this.$store.state.vehicle.indexVideoActive[1]]
        let input
        const uid = uuidv4()
        if (!this.isUsingWsFlv) {
          if (deviceType === 'jc') {
            input = {
              jcInput: {
                filename: video.mediaFilename,
              },
            }
          } else if (deviceType === 'bsj') {
            input = {
              bsjInput: {
                channel: `CH${this.addLeadingZero(video.channel)}`,
                endTime: this.formatDateToUtc(video.endTime),
                startTime: this.formatDateToUtc(video.startTime),
              },
            }
          }
          const response = await this.$store.dispatch('vehicle/requestStreamFromDevice', {
            status: true,
            imei: this.$store.state.vehicle.vehicleDetail.imei,
            streamingEndpoint: this.$store.state.vehicle.streamingEndpoint,
            statsDataV2: {
              tvType: 'TV2',
              idRequest: uid,
            },
            ...input,
          })
          if (response.status !== 200) {
            return this.$mcToast.error(this.$t('videoHistory.video.streamError'))
          }
        }

        this.$store.commit('vehicle/setPlaybackUidActive', uid)
      }
      this.errorWhilePlay = false
      this.reconnecting()
    },
    async sendCommandStream() {
      const video =
        this.$store.state.vehicle.vehicleVideos[this.$store.state.vehicle.indexVideoActive[0]]
          .videos[this.$store.state.vehicle.indexVideoActive[1]]
      await this.$store.dispatch('vehicle/requestStreamFromDevice', {
        status: true,
        imei: this.$store.state.vehicle.vehicleDetail.imei,
        streamingEndpoint: this.$store.state.vehicle.streamingEndpoint,
        statsDataV2: {
          tvType: 'TV2',
          idRequest: this.$store.state.vehicle.playbackUidActive,
        },
        bsjInput: {
          channel: `CH${this.addLeadingZero(video.channel)}`,
          endTime: this.formatDateToUtc(video.endTime),
          startTime: this.formatDateToUtc(video.startTime),
        },
      })
    },
    reconnecting() {
      if (this.isMdvrSleep) {
        this.$store.commit('vehicle/setVideoStatusDetail', 'sleep')
      } else {
        this.$store.commit('vehicle/setVideoStatusDetail', 'connecting')
      }
      this.$store.commit('vehicle/setStartTime', this.$store.state.vehicle.source.startTime)
      this.$forceUpdate()
      var refsName = `videoPlayer${this.$store.state.vehicle.vehicleDetail.imei}`
      this.$refs.videoPlayerHistory.$refs[refsName].initialize()
    },

    async requestOnlineCommandStatsDataTryAgain() {
      const video =
        this.$store.state.vehicle.vehicleVideos[this.$store.state.vehicle.indexVideoActive[0]]
          .videos[this.$store.state.vehicle.indexVideoActive[1]]
      const deviceName = this.vehicleDetail.deviceName
      const input =
        deviceName === 'jc'
          ? {
              jcInput: {
                filename: video.mediaFilename,
              },
            }
          : {
              bsjInput: {
                channel: `CH${this.addLeadingZero(video.channel)}`,
                endTime: this.formatDateToUtc(video.endTime),
                startTime: this.formatDateToUtc(video.startTime),
              },
            }

      await this.$store.dispatch('vehicle/requestStreamFromDevice', {
        status: true,
        imei: this.vehicleDetail.imei,
        statsDataV2: {
          tvType: 'TV2',
          idTryAgain: this.$store.state.vehicle.playbackUidActive,
        },
        ...input,
      })
    },
  },
}
</script>
